package dev.kuml.portal.web.share

import dev.kuml.portal.web.AppScope
import dev.kuml.portal.web.api.ApiResult
import dev.kuml.portal.web.api.PortalApi
import dev.kuml.portal.web.api.ShareLinkResponse
import dev.kuml.portal.web.error.ErrorCopy
import dev.kuml.portal.web.i18n.t
import io.kvision.core.Container
import io.kvision.core.onClick
import io.kvision.form.check.CheckBoxInput
import io.kvision.form.check.checkBoxInput
import io.kvision.form.text.TextInput
import io.kvision.form.text.textInput
import io.kvision.html.Div
import io.kvision.html.InputType
import io.kvision.html.button
import io.kvision.html.div
import io.kvision.html.span
import kotlinx.browser.document
import kotlinx.browser.window
import kotlinx.coroutines.launch
import org.w3c.dom.HTMLInputElement
import kotlin.js.Date

/** Returned so the caller (`EditorScreen.kt`'s Teilen-Button) can close/re-open this popover on
 * repeated clicks, mirroring `DiagramTabsHandle`'s callback-return shape. */
public class ShareDialogHandle(
    public val close: () -> Unit,
)

/**
 * R-21's Freigabe-Dialog, montiert als Popover unter dem "Teilen"-Button im
 * `kportal-editor-subheader` -- dieselbe Popover-Form wie `ProjectListScreen`s/`DiagramTabs`s
 * Loesch-Bestaetigung (die einzige bisherige Bestaetigungs-UI in diesem Frontend).
 *
 * Vollstaendig aus KVision-Kind-Widget-Aufrufen gebaut (`div { span(...); button(...) }`), NIE
 * `content =`/`content +=` gemischt mit Kind-Widgets im selben Container, und niemals `innerHTML`
 * -- der frisch erzeugte Token und die Link-Liste sind serverseitige Inhalte.
 */
public fun Container.shareDialog(
    api: PortalApi,
    diagramId: String,
    onError: (message: String, detail: String?) -> Unit,
): ShareDialogHandle {
    val root = div(className = "kportal-share-dialog")
    root.onClick { e -> e.stopPropagation() }

    val linksHost = root.div(className = "kportal-share-links")
    val newLinkHost = root.div()
    val resultHost = root.div()

    fun expiryLabel(link: ShareLinkResponse): String {
        val expiresAt = link.expiresAt ?: return t.share.neverExpires
        val formatted = t.format.date(expiresAt) ?: expiresAt
        // ISO-8601 instant strings compare correctly lexically -- no date parsing needed.
        val isPast = expiresAt < Date().toISOString()
        return if (isPast) t.share.alreadyExpired else t.share.expiresOn(formatted)
    }

    fun viewedLabel(link: ShareLinkResponse): String {
        val lastViewedAt = link.lastViewedAt ?: return t.share.notYetViewed
        val formatted = t.format.date(lastViewedAt) ?: lastViewedAt
        return t.share.viewedOn(formatted)
    }

    lateinit var reloadLinks: () -> Unit
    lateinit var renderNewLinkForm: () -> Unit

    fun renderLinkRow(
        host: Div,
        link: ShareLinkResponse,
    ) {
        val row = host.div(className = "kportal-share-link-row")
        row.div(className = "kportal-share-link-meta") {
            span(expiryLabel(link))
            if (link.passwordProtected) span(t.share.passwordProtectedBadge, className = "kportal-share-badge")
            span(viewedLabel(link))
        }
        val actions = row.div(className = "kportal-share-link-actions")
        val revokeButton = actions.button(t.share.revokeButton, className = "kportal-btn kportal-btn-text-neutral kportal-btn-auto")
        val confirmHost = row.div()
        revokeButton.onClick { e ->
            e.stopPropagation()
            confirmHost.removeAll()
            val popover = confirmHost.div(className = "kportal-popover")
            popover.onClick { ev -> ev.stopPropagation() }
            popover.span(t.share.revokeConfirmQuestion)
            val popoverActions = popover.div(className = "kportal-popover-actions")
            val cancel = popoverActions.button(t.common.cancel, className = "kportal-btn kportal-btn-secondary kportal-btn-auto")
            val confirm = popoverActions.button(t.share.revokeButton, className = "kportal-btn kportal-btn-text")
            cancel.onClick { ev ->
                ev.stopPropagation()
                confirmHost.removeAll()
            }
            confirm.onClick { ev ->
                ev.stopPropagation()
                confirm.disabled = true
                cancel.disabled = true
                AppScope.launch {
                    when (api.revokeShareLink(link.id)) {
                        is ApiResult.Success -> reloadLinks()
                        else -> {
                            confirm.disabled = false
                            cancel.disabled = false
                            onError(t.share.createFailed, null)
                        }
                    }
                }
            }
        }
    }

    reloadLinks = {
        AppScope.launch {
            when (val result = api.listShareLinks(diagramId)) {
                is ApiResult.Success -> {
                    linksHost.removeAll()
                    linksHost.div(t.share.existingLinksHeading, className = "kportal-label")
                    if (result.value.isEmpty()) {
                        linksHost.div(t.share.noLinksYet, className = "kportal-attach-hint")
                    } else {
                        result.value.forEach { link -> renderLinkRow(linksHost, link) }
                    }
                }
                is ApiResult.HttpError -> onError(t.errors.forCode(result.error?.code, result.status) ?: t.share.createFailed, null)
                ApiResult.NetworkError -> onError(ErrorCopy.forNetworkError(t.errors).message, null)
            }
        }
    }

    fun showResult(token: String) {
        resultHost.removeAll()
        val url = ShareUrl.forToken(window.location.origin, token)
        resultHost.div(className = "kportal-field") {
            val urlInput: TextInput = textInput(value = url, className = "kportal-input kportal-share-url")
            urlInput.getElement()?.setAttribute("readonly", "true")
            val copyButton = button(t.share.copyButton, className = "kportal-btn kportal-btn-secondary kportal-btn-auto")
            val copiedSpan = span("", className = "kportal-status-success")
            copyButton.onClick { e ->
                e.stopPropagation()
                val element = urlInput.getElement() as? HTMLInputElement
                element?.select()
                runCatching {
                    window.navigator
                        .asDynamic()
                        .clipboard
                        .writeText(url)
                }
                copiedSpan.content = t.share.copied
            }
        }
        resultHost.div(t.share.resultHint, className = "kportal-attach-hint")
    }

    renderNewLinkForm = {
        newLinkHost.removeAll()
        newLinkHost.div(t.share.newLinkHeading, className = "kportal-label")

        val passwordRow = newLinkHost.div(className = "kportal-field")
        val passwordCheck: CheckBoxInput = passwordRow.checkBoxInput(value = false)
        passwordRow.span(t.share.passwordCheckboxLabel)
        val passwordFieldHost = newLinkHost.div()

        val expiryRow = newLinkHost.div(className = "kportal-field")
        val expiryCheck: CheckBoxInput = expiryRow.checkBoxInput(value = false)
        expiryRow.span(t.share.expiryCheckboxLabel)
        val expiryFieldHost = newLinkHost.div()

        var passwordInput: TextInput? = null
        var expiryInputElement: HTMLInputElement? = null

        passwordCheck.onClick {
            passwordFieldHost.removeAll()
            passwordInput = null
            if (passwordCheck.value) {
                val input =
                    passwordFieldHost.textInput(type = InputType.PASSWORD, className = "kportal-input")
                input.placeholder = t.share.passwordPlaceholder
                passwordInput = input
            }
        }

        expiryCheck.onClick {
            expiryFieldHost.removeAll()
            expiryInputElement = null
            if (expiryCheck.value) {
                // KVision has no dedicated date-picker widget -- a hidden-native-input pattern
                // mirrors `EditorScreen.kt`'s attach-a-file `<input type="file">` convention:
                // created via document.createElement, attached to a KVision container's own DOM
                // node, never via innerHTML.
                val input = document.createElement("input") as HTMLInputElement
                input.type = "date"
                expiryFieldHost.getElement()?.appendChild(input)
                expiryInputElement = input
            }
        }

        val createButton = newLinkHost.button(t.share.createButton, className = "kportal-btn kportal-btn-primary kportal-btn-auto")
        val errorSpan = newLinkHost.span("", className = "kportal-status-error")
        createButton.onClick { e ->
            e.stopPropagation()
            errorSpan.content = ""
            val password = passwordInput?.value?.takeIf { it.isNotBlank() }
            val expiryDate = expiryInputElement?.value?.takeIf { it.isNotBlank() }
            // A bare "YYYY-MM-DD" date input value, sent as end-of-day UTC so the server's
            // `expiresAt > now` check does not reject "today" chosen in a timezone ahead of UTC.
            val expiresAt = expiryDate?.let { "${it}T23:59:59Z" }

            createButton.disabled = true
            createButton.text = t.share.creating
            AppScope.launch {
                when (val result = api.createShareLink(diagramId, password, expiresAt)) {
                    is ApiResult.Success -> {
                        showResult(result.value.token)
                        reloadLinks()
                        renderNewLinkForm()
                    }
                    is ApiResult.HttpError -> {
                        createButton.disabled = false
                        createButton.text = t.share.createButton
                        errorSpan.content = t.errors.forCode(result.error?.code, result.status) ?: t.share.createFailed
                    }
                    ApiResult.NetworkError -> {
                        createButton.disabled = false
                        createButton.text = t.share.createButton
                        errorSpan.content = ErrorCopy.forNetworkError(t.errors).message
                    }
                }
            }
        }
    }

    reloadLinks()
    renderNewLinkForm()

    return ShareDialogHandle(close = { root.removeAll() })
}
